import { createDirectionalShadows } from 'ravenjs/utils/shadows';
import { createTransitions } from 'ravenjs/utils/transitions';
import { generateMedia } from 'ravenjs/utils/media';

import { darken } from 'polished';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';
import { KEYS, VALUES, UNIT } from './breakpoints';
import { PALETTE } from './colors';
import { CSS_EFFECTS } from './effects';
import { GRID } from './grid';
import { TYPOGRAPHY } from './typography';

const THEME_COLORS = PALETTE.COLORS;
const INTERACTIVE_COLOR = THEME_COLORS.INTERACTIVE || THEME_COLORS.AZURE;

/**
 * Create breakpoints for the current theme
 *
 * @method createBreakpoints
 * @return {Object}          The map containing all of the breakpoint functions and values.
 */
export const createBreakpoints = () => ({
    keys: KEYS,
    unit: UNIT,
    values: VALUES,
});

/**
 * The default theme for the application
 *
 * @type {Object}
 */
export const THEME = {
    // build the breakpoints and its utilities
    breakpoints: createBreakpoints(),
    // define the set of default colors
    colors: THEME_COLORS,
    // build the CSS in JS transitions / effects
    effects: CSS_EFFECTS,
    // build the grid properties
    grid: GRID,
    // build the media utilities
    media: generateMedia(),
    // build the color palette
    palette: PALETTE,
    // build the transitions utilities
    transitions: createTransitions(),
    // build the typographic styles
    typography: TYPOGRAPHY,
    // build the shadows
    shadows: createDirectionalShadows(),
    DescriptionField: {
        styles: {
            root: {
                padding: '0 0 5px',
                borderBottom: `1px solid ${THEME_COLORS.DIVIDER}`,
                marginBottom: '10px !important',
            },
        },
    },
    TitleField: {
        styles: {
            root: {
                color: THEME_COLORS.BLACK,
                fontSize: '20px',
                marginLeft: 0,
            },
        },
    },
    TableRow: {
        styles: {
            backgroundColor: THEME_COLORS.WHITE,
            selected: {
                backgroundColor: MIGRATED_COLORS.ravenInputDisabled,
            },
            zebra: {
                backgroundColor: MIGRATED_COLORS.tableGrey,
            },
        },
    },
    TableCell: {
        styles: {
            portrait: {
                display: 'flex !important',
                '&:first-of-type': {
                    borderTop: 'none',
                },
            },
        },
        body: {
            styles: {},
        },
        head: {
            styles: {
                borderTop: 'none',
                color: THEME_COLORS.BLACK2,
                fontSize: '1rem',
            },
        },
        sortable: {
            styles: {
                borderTop: 'none',
                color: THEME_COLORS.BLACK2,
                cursor: 'pointer',
                fontSize: '14px',
            },
        },
    },
    ImpersonateUserHeader: {
        styles: {
            backgroundColor: 'white',
            color: MIGRATED_COLORS.literal333,
            fontSize: '18px',
            padding: '0px 20px',
        },
        height: 62,
    },
    AppNavigation: {
        styles: {
            backgroundColor: 'white',
        },
        height: 62,
    },
    SubNavItem: {
        styles: {
            color: INTERACTIVE_COLOR,
            '&:active': {
                color: darken(0.15, INTERACTIVE_COLOR),
            },
            '&:hover': {
                color: darken(0.15, INTERACTIVE_COLOR),
            },
        },
        active: {
            borderBottomColor: INTERACTIVE_COLOR,
        },
    },
    DragAndDrop: {
        styles: {
            container: {
                background: THEME_COLORS.WHITE,
            },
            draggable: {
                userSelect: 'none',
                padding: '5px',
                background: THEME_COLORS.GREY_3,
            },
            dragging: {
                background: THEME_COLORS.GREY,
                border: `dashed 1px ${THEME_COLORS.GREY_5}`,
            },
        },
    },
};
